import React from "react";
import { IconButton } from "@material-ui/core";
import WithPopover from "@components/WithPopover";
import { Body, Spinner, Subheading } from "@components";
import { Delete } from "mdi-material-ui";
import locale from "@bonliva-crm/locale";
import { useCustomerCard_GetDeleteInfoQuery } from "@graphql";

type CustomerDeleteButtonProps = {
  customerId: string;
  onDelete: () => void;
};

export const CustomerDeleteButton: React.FC<CustomerDeleteButtonProps> = ({
  customerId,
  onDelete
}) => {
  const { data, loading, error, refetch } = useCustomerCard_GetDeleteInfoQuery({
    variables: { id: customerId }
  });

  const deleteInfo = data?.customerById;
  const canBeDeleted = deleteInfo?.CanBeDeleted;
  const reasons: string[] = deleteInfo?.CantBeDeletedReasons || [];

  let popoverContent: React.ReactNode = null;

  if (loading) {
    popoverContent = (
      <div style={{ padding: 8, display: "flex", alignItems: "center" }}>
        <Body style={{ marginLeft: 8 }}>{"Loading..."}</Body>
      </div>
    );
  } else if (error) {
    popoverContent = (
      <div style={{ padding: 8 }}>
        <Subheading color="error">{locale.Error || "Error"}</Subheading>
        <Body block>{error.message}</Body>
        <Body
          block
          style={{
            marginTop: 8,
            cursor: "pointer",
            textDecoration: "underline"
          }}
          onClick={() => refetch()}
        >
          {locale.Retry || "Retry"}
        </Body>
      </div>
    );
  } else if (deleteInfo && !canBeDeleted) {
    popoverContent = (
      <div style={{ padding: 8 }}>
        <Subheading color="error">{locale.CustomerCantBeDeleted}</Subheading>

        {reasons.map((reason, i) => (
          <Body block key={i}>
            {locale.CantBeDeletedReasons[reason] || reason}
          </Body>
        ))}
      </div>
    );
  }

  return (
    <WithPopover
      key="delete"
      popoverContent={popoverContent}
      PopoverProps={{
        anchorOrigin: { horizontal: "center", vertical: "bottom" },
        transformOrigin: { horizontal: "center", vertical: "top" }
      }}
    >
      <IconButton
        disabled={
          loading || (!!deleteInfo && !canBeDeleted) || !!error // optional: disable on error
        }
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();

          if (loading || (!loading && deleteInfo && !canBeDeleted)) {
            return;
          }

          if (deleteInfo && canBeDeleted) {
            onDelete();
          } else if (!deleteInfo) {
            refetch();
          }
        }}
      >
        {loading ? <Spinner size={24} /> : <Delete />}
      </IconButton>
    </WithPopover>
  );
};
